'use client';

import CustomLoading from '@/components/custom-loading';
import TableAG from '@/components/custom-table-ag';
import { pageDirectories } from '@/constant/route';
import { useCountMenuFilter } from '@/hooks/use-count-menu-filter';
import { useKeyboardShortcut } from '@/hooks/use-keyboard-shortcut';
import { SidebarItemProps, useSidebarStore } from '@/hooks/use-sidebar-store';
import { cn } from '@/lib/utils';
import { Link, usePathname } from '@/navigation';
import { MessageCircleQuestion } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { CustomMenuOverlay } from '../../components/custom-menu';
import { CustomLayoutSidebarProps } from '../../types';
import { useSidebarState } from '../../ulti';
import { CustomSidebarItem } from './sidebar-item';
import SidebarSearch from './sidebar-search';

const mediaQueryBreakdown = 639;

export function CustomSidebar({
    className,
    items,
    ...props
}: CustomLayoutSidebarProps) {
    const pathname = usePathname();
    const transDef = useTranslations('System');

    const { triggerSizeColumnToFit } = TableAG.useTableAG();

    const {
        isShowSidebarIconOnly,
        setIsLoading,
        setObjActive,
        isShowMenuOverlay,
        setIsShowMenuOverlay,
        setMenus,
        setMenuForSearch,
        setIsShowSidebarIconOnly,
        menus,
        menuForSearch,
    } = useSidebarStore((x) => x);

    const [searchItems, setSearchItems] = useState<SidebarItemProps[]>([]);

    const {
        isDesktop = true, // Default là true
        keyword,
        setKeyword,
        arrMenuPath,
        isInitial,
    } = useSidebarState(
        pathname,
        items,
        menuForSearch,
        mediaQueryBreakdown,
        setMenuForSearch,
        setObjActive,
        setIsShowSidebarIconOnly,
        setMenus,
        setIsLoading,
        setSearchItems
    );
    console.log('items', { items });

    useCountMenuFilter(arrMenuPath);
    useKeyboardShortcut(
        () => {
            setIsShowSidebarIconOnly((st) => !st);
            setTimeout(() => {
                triggerSizeColumnToFit?.();
            }, 200);
        },
        { key: 'B', ctrlKey: true }
    );

    const isSearch = keyword.length > 0;
    const arr = isSearch ? searchItems : menus;
    const content = (
        <nav className={cn('relative flex flex-col pb-6')}>
            <div
                className={cn(
                    'bg-sidebar shadow-b-md md:sticky md:top-0 md:z-10'
                )}
            >
                <SidebarSearch
                    className={cn('py-1 pr-2')}
                    keyword={keyword}
                    setKeyword={setKeyword}
                />
            </div>
            {isInitial ? (
                <div className='flex min-h-32 items-center justify-center'>
                    <CustomLoading.Round size='small' />
                </div>
            ) : menus.length === 0 ? (
                <div className='my-4'>
                    <div className='mx-auto flex flex-col items-center justify-center gap-1 px-2'>
                        <MessageCircleQuestion
                            size={48}
                            className='fill-primary text-white'
                        />
                        <span className='text-center text-sm'>
                            Some error occurred while handle the menu.
                            <br />
                            Please reload website or{' '}
                            <Link
                                href={pageDirectories['Contact']}
                                target='_blank'
                                rel='noreferrer noopener'
                                className='text-primary underline'
                            >
                                contact admin
                            </Link>
                            .
                        </span>
                    </div>
                </div>
            ) : (
                <>
                    {arr.map((x, key) => (
                        <CustomSidebarItem
                            className='pr-1'
                            isDesktop={isDesktop}
                            pathname={pathname}
                            item={x}
                            key={key}
                            isSearch={isSearch}
                        />
                    ))}

                    {isSearch && arr.length === 0 && (
                        <div className='my-3 flex items-center justify-center'>
                            {transDef('System_NoDataFound')}
                        </div>
                    )}
                </>
            )}
        </nav>
    );

    const hvSignalRInfo = useSidebarStore((state) => state.showTotalBox);

    if (!isDesktop) {
        return (
            <CustomMenuOverlay
                open={isShowMenuOverlay}
                setOpen={setIsShowMenuOverlay}
            >
                {content}
            </CustomMenuOverlay>
        );
    }

    return (
        <aside
            className={cn(
                'hidden flex-col overflow-hidden bg-sidebar text-sm sm:block',
                'scroll h-full w-full pb-1 transition-[width] duration-150 ease-in-out',
                isShowSidebarIconOnly ? 'w-16' : 'w-72',
                className,
                {
                    'sm:h-[calc(100vh-theme(height.12))]': !hvSignalRInfo,
                }
            )}
            {...props}
        >
            {content}
        </aside>
    );
}
